import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Part } from '../../types';
import { getDefaultDescription } from '../../lib/partDescriptions';
import EmojiPicker from './EmojiPicker';
import { motion, AnimatePresence } from 'framer-motion';

interface AddPartModalProps {
  onClose: () => void;
  onAdd: (part: Omit<Part, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  part?: Part;
}

export default function AddPartModal({ onClose, onAdd, part }: AddPartModalProps) {
  const [formData, setFormData] = useState<Omit<Part, 'id' | 'user_id' | 'created_at' | 'updated_at'>>({
    name: part?.name || '',
    type: part?.type || 'protector',
    description: part?.description || getDefaultDescription('protector'),
    status: part?.status || 'active',
    emotions: part?.emotions || [],
    notes: part?.notes || [],
    x: part?.x || 0,
    y: part?.y || 0,
    color: part?.color || 'blue',
    emoji: part?.emoji || '🛡️'
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newEmotion, setNewEmotion] = useState('');

  const handleTypeChange = (type: Part['type']) => {
    setFormData(prev => ({
      ...prev,
      type,
      description: getDefaultDescription(type),
      emoji: type === 'self' ? '🌟' :
             type === 'protector' ? '🛡️' :
             type === 'manager' ? '📋' :
             type === 'rescuer' ? '🚑' :
             type === 'exile' ? '🌱' : '🛡️'
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onAdd(formData);
      onClose();
    } catch (err) {
      console.error('Error saving part:', err);
      setError(err instanceof Error ? err.message : 'Failed to save part');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEmotion = () => {
    if (!newEmotion.trim() || formData.emotions.includes(newEmotion.trim())) return;
    setFormData(prev => ({
      ...prev,
      emotions: [...prev.emotions, newEmotion.trim()]
    }));
    setNewEmotion('');
  };

  const handleEmojiSelect = (emoji: string, color: string) => {
    setFormData(prev => ({
      ...prev,
      emoji,
      color
    }));
    setShowEmojiPicker(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-800">
              {part ? 'Edit Part' : 'Add New Part'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form id="partForm" onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['self', 'protector', 'exile', 'rescuer', 'manager'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeChange(type as Part['type'])}
                    className={`
                      p-3 rounded-lg border-2 text-left transition-all
                      ${formData.type === type
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="font-medium capitalize text-gray-700">
                      {type}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emoji & Color Theme
              </label>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(true)}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-amber-500 transition-all text-center"
              >
                <span className="text-4xl mb-2 block">{formData.emoji}</span>
                <span className="text-sm text-gray-600">Click to change</span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Part['status'] }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="active">Active</option>
                <option value="trusted">Trusted</option>
                <option value="unburdened">Unburdened</option>
                <option value="integrated">Integrated</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Emotions
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.emotions.map(emotion => (
                  <span
                    key={emotion}
                    className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {emotion}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        emotions: prev.emotions.filter(e => e !== emotion)
                      }))}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newEmotion}
                  onChange={(e) => setNewEmotion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmotion())}
                  placeholder="Add emotion..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={handleAddEmotion}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="partForm"
              disabled={saving}
              className="bg-amber-500 text-gray-800 px-4 py-2 rounded-lg hover:bg-amber-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : part ? 'Update Part' : 'Add Part'}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            >
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Choose an Emoji
                  </h3>
                  <button
                    onClick={() => setShowEmojiPicker(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <EmojiPicker
                  onSelect={handleEmojiSelect}
                  partType={formData.type}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}